import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { IPatientListMeasurement } from "../../../../../../@types/Patient";
import { TrendArrowDown } from "../../../../../../assets/icons/TrendArrowDown";
import { TrendArrowUp } from "../../../../../../assets/icons/TrendArrowUp";
import { TooltipLine } from "../../../../../../components/TooltipLine";
import { getLongFormattedDate } from "../../../../../../utils/dateFormatter";
import {
  RagCircle,
  RagIndicatorContainer,
  RagValue,
  TrendArrow,
} from "./RagIndicator.styles";

interface IRagIndicatorProps {
  measurement: IPatientListMeasurement | null | undefined;
  formatValue?: (value: number) => string;
}

const defaultFormat = (value: number) =>
  Number.isInteger(value) ? value.toString() : value.toFixed(1);

export function RagIndicator({
  measurement,
  formatValue = defaultFormat,
}: IRagIndicatorProps) {
  const { t } = useTranslation();

  if (!measurement || measurement.value === null) {
    return <>-</>;
  }

  const { value, previousValue, recordedAt, ragColor, ragLabel } = measurement;
  const formattedValue = formatValue(value);

  let trend: "up" | "down" | null = null;
  if (typeof previousValue === "number" && previousValue !== value) {
    trend = value > previousValue ? "up" : "down";
  }

  const tooltipContent = (
    <>
      <TooltipLine emphasis>{formattedValue}</TooltipLine>
      {recordedAt && (
        <TooltipLine>{getLongFormattedDate(recordedAt)}</TooltipLine>
      )}
      {ragLabel && <TooltipLine strong>{ragLabel}</TooltipLine>}
    </>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow={false}
      color="#4B4B4B"
      mouseLeaveDelay={0}
      styles={{
        body: {
          padding: "16px 20px",
          borderRadius: "4px !important",
        },
      }}
    >
      <RagIndicatorContainer>
        <RagValue>{formattedValue}</RagValue>
        {ragColor && <RagCircle style={{ backgroundColor: ragColor }} />}
        {trend === "up" && (
          <TrendArrow
            aria-label={t("patientList_rag_trendUp", {
              previousValue,
              value,
            })}
          >
            <TrendArrowUp />
          </TrendArrow>
        )}
        {trend === "down" && (
          <TrendArrow
            aria-label={t("patientList_rag_trendDown", {
              previousValue,
              value,
            })}
          >
            <TrendArrowDown />
          </TrendArrow>
        )}
      </RagIndicatorContainer>
    </Tooltip>
  );
}
